import React from "react";
import { User, Mail, Calendar, Shield, Settings, Key, Bell } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";

const AccountDetailsCard = ({ icon: Icon, label, value, className }) => (
  <div className={`bg-white rounded-lg shadow-sm ${className}`}>
    <div className="flex items-center p-6">
      <div className="rounded-full bg-blue-50 p-3">
        <Icon className="w-6 h-6 text-blue-600" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const TabButton = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg ${
      active
        ? "bg-white text-blue-600 shadow-sm"
        : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);

const Account = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState("profile");
  
  const formatDate = (date) => {
    return new Date(date || Date.now()).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar - hidden on mobile */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:ml-64">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
              <p className="mt-1 text-gray-500">Manage your account preferences and settings</p>
            </div>
            
          </div>

          {/* Tabs */}
          <div className="bg-gray-100 p-1 rounded-lg max-w-md mb-8 flex space-x-1">
            <TabButton
              active={activeTab === "profile"}
              onClick={() => setActiveTab("profile")}
            >
              Profile
            </TabButton>
            <TabButton
              active={activeTab === "security"}
              onClick={() => setActiveTab("security")}
            >
              Security
            </TabButton>
            <TabButton
              active={activeTab === "notifications"}
              onClick={() => setActiveTab("notifications")}
            >
              Notifications
            </TabButton>
          </div>

          {/* Tab Content */}
          {activeTab === "profile" && (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="flex flex-col sm:flex-row items-center p-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="mt-4 sm:mt-0 sm:ml-6 text-center sm:text-left">
                    <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
                    <p className="text-gray-500">{user?.role || 'User'}</p>
                    
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AccountDetailsCard 
                  icon={User} 
                  label="Full Name" 
                  value={user?.name} 
                />
                <AccountDetailsCard 
                  icon={Mail} 
                  label="Email Address" 
                  value={user?.email} 
                />
                <AccountDetailsCard 
                  icon={Shield} 
                  label="Account Type" 
                  value={user?.role || 'Standard User'} 
                />
                <AccountDetailsCard 
                  icon={Calendar} 
                  label="Member Since" 
                  value={formatDate(user?.createdAt)} 
                />
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Key className="w-5 h-5 text-blue-600 mr-4" />
                      <div>
                        <p className="font-medium">Password</p>
                        <p className="text-sm text-gray-500">Change Password</p>
                        <p className="text-sm text-red-500">Coming soon!!!...you can change the password on the signup page for now</p>
                      </div>
                    </div>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      Change
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Bell className="w-5 h-5 text-blue-600 mr-4" />
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-gray-500">Receive updates about your account</p>
                        <p className="text-sm text-red-500">Coming soon!!!</p>
                      </div>
                    </div>
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                      Configure
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;